import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import apiClient from '../api/client';

interface UserContext {
  userId: string;
  organizationId: string;
  workspaceId: string | null;
  role: string | null;
}

interface TenantContextType {
  organizationId: string | null;
  workspaceId: string | null;
  setWorkspaceId: (id: string) => void;
  loading: boolean;
  error: string | null;
  selectOrganization: (id: string) => void;
}

const TenantContext = createContext<TenantContextType>({
  organizationId: null,
  workspaceId: null,
  setWorkspaceId: () => {},
  error: null,
  selectOrganization: () => {},
  loading: true
});

export const TenantProvider = ({ children }: { children: React.ReactNode }) => {
  const { user } = useAuth();
  const [organizationId, setOrganizationId] = useState<string | null>(null);
  const [workspaceId, setWorkspaceId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selection, setSelection] = useState<{uid: string; id: string} | null>(null);
  const selectedOrganization = selection?.uid === user?.uid ? selection?.id : undefined;

  useEffect(() => {
    let active = true;
    const resolveTenant = async () => {
      setLoading(true);
      setError(null);
      setOrganizationId(null);
      setWorkspaceId(null);
      localStorage.removeItem('spark_org_id');
      localStorage.removeItem('spark_workspace_id');
      if (!user) {
        setOrganizationId(null);
        setWorkspaceId(null);
        setLoading(false);
        return;
      }

      try {
        // Authoritative Context Discovery via Backend
        const response = await apiClient.get<UserContext>('/api/v1/auth/context', { sparkDiscoveryOrganization: selectedOrganization });
        if (!active) return;
        const context = response.data;

        if (context.userId !== user.uid || !context.organizationId ||
            (selectedOrganization && context.organizationId !== selectedOrganization)) {
          throw new Error('Context identity mismatch');
        }
        if (context.organizationId) {
          setOrganizationId(context.organizationId);
          localStorage.setItem('spark_org_id', context.organizationId);

          const targetWs = context.workspaceId ?? null;
          setWorkspaceId(targetWs);
          if (targetWs) localStorage.setItem('spark_workspace_id', targetWs);
          else localStorage.removeItem('spark_workspace_id');
        }
      } catch (error) {
        // Do not expose Axios request configuration or credentials in diagnostics.
        if (!active) return;
        setError('Organization access could not be confirmed. Choose your organization below; the server will verify access.');
        setOrganizationId(null);
        setWorkspaceId(null);
        localStorage.removeItem('spark_org_id');
        localStorage.removeItem('spark_workspace_id');
      } finally {
        if (active) setLoading(false);
      }
    };

    resolveTenant();
    return () => { active = false; };
  }, [user, selection]);

  const selectOrganization = (id: string) => {
    const value = id.trim();
    if (!user || !/^org_[A-Za-z0-9_-]{1,60}$/.test(value)) {
      setError('Enter a valid organization ID supplied by your administrator.');
      return;
    }
    setOrganizationId(null); setWorkspaceId(null); setLoading(true);
    setSelection({uid: user.uid, id: value});
  };

  const updateWorkspace = (id: string) => {
    setWorkspaceId(id);
    localStorage.setItem('spark_workspace_id', id);
  };

  return (
    <TenantContext.Provider value={{ organizationId, workspaceId, setWorkspaceId: updateWorkspace, loading, error, selectOrganization }}>
      {children}
    </TenantContext.Provider>
  );
};

export const useTenant = () => useContext(TenantContext);
